import { useMemo } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import { EnterpriseFilterOptions_FilterType as FilterType } from '__generated__/graphql-types';

import { useThirdPartyOrganization } from 'bundles/admin-dashboard/components/ThirdPartyOrganizationProvider';
import RoleTemplatesByOrganizationQuery from 'bundles/enterprise-admin-management/api/RoleTemplatesByOrganization.graphql';
import type {
  RoleTemplatesByOrganizationQuery as Query,
  RoleTemplatesByOrganizationQueryVariables as Variables,
} from 'bundles/enterprise-admin-management/api/__generated__/RoleTemplatesByOrganization';
import {
  MENTOR_TEMPLATE_ID,
  ORG_ADMIN_TEMPLATE_ID,
  PROGRAM_ADMIN_TEMPLATE_ID,
  RECRUITER_TEMPLATE_ID,
  STAFF_TEMPLATE_ID,
  TEMPLATE_ID,
} from 'bundles/enterprise-admin-management/constants';
import inviteMutation from 'bundles/enterprise-admin-management/queries/InviteAdmins.graphql';
import allAdminsQuery from 'bundles/enterprise-admin-management/queries/QueryAllAdmins.graphql';
import SearchFilterOptions from 'bundles/enterprise-admin-management/queries/SearchFilterOptions.graphql';
import SearchFilterOptionsProgramLevel from 'bundles/enterprise-admin-management/queries/SearchFilterOptionsProgramLevel.graphql';
import validationMutation from 'bundles/enterprise-admin-management/queries/ValidateInvitations.graphql';
import type {
  EnterpriseAdmin_CreateInvitationsOrRolesMutation,
  EnterpriseAdmin_CreateInvitationsOrRolesMutationVariables,
} from 'bundles/enterprise-admin-management/queries/__generated__/InviteAdmins';
import type {
  QueryAllAdminsQuery,
  QueryAllAdminsQueryVariables,
} from 'bundles/enterprise-admin-management/queries/__generated__/QueryAllAdmins';
import type {
  SearchFilterOptionsQuery,
  SearchFilterOptionsQueryVariables,
} from 'bundles/enterprise-admin-management/queries/__generated__/SearchFilterOptions';
import type {
  SearchFilterOptionsProgramLevelQuery,
  SearchFilterOptionsProgramLevelQueryVariables,
} from 'bundles/enterprise-admin-management/queries/__generated__/SearchFilterOptionsProgramLevel';
import type {
  EnterpriseAdmin_ValidateInvitationsOrRolesMutation,
  EnterpriseAdmin_ValidateInvitationsOrRolesMutationVariables,
} from 'bundles/enterprise-admin-management/queries/__generated__/ValidateInvitations';
import type { AdminRole } from 'bundles/enterprise-admin-management/types';
import { useEnterpriseAdminPermissions } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';

export const useTemplateIdsQuery = (roles: string[]) => {
  const thirdPartyOrganization = useThirdPartyOrganization();
  const { loading, error, data } = useQuery<Query, Variables>(RoleTemplatesByOrganizationQuery, {
    variables: { organizationId: thirdPartyOrganization.id },
    context: { clientName: 'gatewayGql' },
  });

  const roleTemplates = data?.EnterpriseRoleTemplate?.queryRoleTemplatesByOrganization;
  const templateIds = roles.length > 0 ? roles : roleTemplates?.map((_) => _.id) ?? [];
  const filteredTemplateIds = templateIds.filter((templateId) => {
    let result = false;
    if (templateId === MENTOR_TEMPLATE_ID) {
      result = thirdPartyOrganization.organizationType === 'CAMPUS';
    }
    if (
      templateId === ORG_ADMIN_TEMPLATE_ID ||
      templateId === PROGRAM_ADMIN_TEMPLATE_ID ||
      templateId === STAFF_TEMPLATE_ID ||
      templateId === RECRUITER_TEMPLATE_ID
    ) {
      result = true;
    }
    return result;
  });
  return { filteredTemplateIds, loading, error };
};

export const useInviteValidation = () => {
  const [validate, validationState] = useMutation<
    EnterpriseAdmin_ValidateInvitationsOrRolesMutation,
    EnterpriseAdmin_ValidateInvitationsOrRolesMutationVariables
  >(validationMutation);
  const org = useThirdPartyOrganization();
  return [
    async ({
      emails,
      programIds = [],
      adminRole,
    }: {
      emails: string[];
      programIds?: string[];
      adminRole: AdminRole;
    }) => {
      const response = await validate({
        variables: {
          input: {
            emails,
            organizationId: org.id,
            programIds,
            roleTemplateId: TEMPLATE_ID[adminRole],
          },
        },
        context: { clientName: 'gatewayGql' },
      });
      const invalidAdminCreates = response.data?.EnterpriseAdmin_ValidateInvitationsOrRoles.invalidAdminCreates ?? [];
      return invalidAdminCreates;
    },
    validationState,
  ] as const;
};

export const useAllAdminsQuery = ({
  templateIds,
  filteredProgramIds,
  skip,
}: {
  templateIds: string[];
  filteredProgramIds: string[];
  skip?: boolean;
}) => {
  const thirdPartyOrganization = useThirdPartyOrganization();
  const variables: QueryAllAdminsQueryVariables = {
    organizationId: thirdPartyOrganization.id,
    programIds: filteredProgramIds,
    templateIds,
  };
  const { data, error, loading, refetch } = useQuery<QueryAllAdminsQuery, QueryAllAdminsQueryVariables>(
    allAdminsQuery,
    {
      variables,
      skip: skip || templateIds.length === 0,
      context: { clientName: 'gatewayGql' },
      fetchPolicy: 'network-only',
    }
  );
  const allAdmins = data?.EnterpriseUserRoles?.queryAllAdmins;
  const sortedAdmins = useMemo(() => {
    return [...(allAdmins ?? [])].sort((a, b) => {
      if (!b.roles[0].createdAt || !a.roles[0].createdAt) return 0;
      return new Date(b.roles[0].createdAt).getTime() - new Date(a.roles[0].createdAt).getTime();
    });
  }, [allAdmins]);
  return { allAdmins: sortedAdmins, error, loading, refetchAllAdmins: () => refetch(variables) };
};

export const useInviteAdmins = () => {
  const org = useThirdPartyOrganization();
  const [invite, invitationState] = useMutation<
    EnterpriseAdmin_CreateInvitationsOrRolesMutation,
    EnterpriseAdmin_CreateInvitationsOrRolesMutationVariables
  >(inviteMutation);
  return [
    async ({
      emails,
      programIds = [],
      adminRole,
      sendInvitation,
    }: {
      emails: string[];
      programIds?: string[];
      adminRole: AdminRole;
      sendInvitation: boolean;
    }) => {
      const response = await invite({
        variables: {
          input: {
            emails,
            organizationId: org.id,
            programIds,
            roleTemplateId: TEMPLATE_ID[adminRole],
            skipEmailNotification: !sendInvitation,
          },
        },
        context: { clientName: 'gatewayGql' },
      });
      const invites = response.data?.EnterpriseAdmin_CreateInvitationsOrRoles.invitesSent.map((_) => ({
        email: _.email,
        inviteId: _.invitations[0].id,
        roleTemplateId: _.invitations[0].roleTemplateId ?? undefined,
      }));
      return invites;
    },
    invitationState,
  ] as const;
};

export const useProgramSearch = (organizationId: string, searchWord: string) => {
  const { getIsOrganizationDomainAdmin, getAllProgramIdsWithPermission } = useEnterpriseAdminPermissions();

  const { data: orgData, loading: loadingOrgData } = useQuery<
    SearchFilterOptionsQuery,
    SearchFilterOptionsQueryVariables
  >(SearchFilterOptions, {
    variables: {
      organizationId,
      filterType: FilterType.EnterpriseFilterOptionTypeProgram,
      searchWord,
    },
    context: {
      clientName: 'gatewayGql',
    },
    skip: !getIsOrganizationDomainAdmin(),
  });

  const { data: programData, loading: loadingProgramData } = useQuery<
    SearchFilterOptionsProgramLevelQuery,
    SearchFilterOptionsProgramLevelQueryVariables
  >(SearchFilterOptionsProgramLevel, {
    variables: {
      programIds: getAllProgramIdsWithPermission('DEFAULT_SUBDOMAIN', 'VIEW'),
      filterType: FilterType.EnterpriseFilterOptionTypeProgram,
      searchWord,
    },
    context: {
      clientName: 'gatewayGql',
    },

    skip: getIsOrganizationDomainAdmin(),
  });

  const foundPrograms: { id: string; label: string }[] = filterExistsOrDefault(
    orgData?.EnterpriseFilterOptions?.searchFilterOptions?.edges?.map((e) => e?.node) ??
      programData?.EnterpriseFilterOptions?.searchFilterOptionsProgramLevel?.edges?.map((e) => e?.node)
  );
  return { foundPrograms, loadingPrograms: loadingOrgData || loadingProgramData };
};
